/**
 * CareNeedsBrowse — PUBLIC Care Needs browsing (2026-08-24 follow-up to
 * Step 3). No sign-in required to view listings — anyone can see what's
 * published. Expressing interest requires sign-in/free registration,
 * matched via GET /api/care-needs/public (see care_needs.py — identical
 * carer-safe projection to the authenticated /published endpoint, just
 * reachable without a session). Signed-in carers (any tier — carer_free,
 * carer_profile/Carer Essentials, pro_carer) see live interest_status and
 * can express interest directly, exactly as in the workspace version at
 * /workspace/pro-carer-circle/care-opportunities.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { MapPin, Clock, CalendarClock, PoundSterling, Compass, CheckCircle2, HandHeart, LogIn, ChevronDown, ChevronUp, Info } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { PageHero } from "../components/shared/Primitives";
import { useHandoverSession } from "../hooks/useHandoverSession";

const CARE_TYPE_LABELS = {
  live_in: "Live-in", hourly: "Hourly visits", waking_night: "Waking nights",
  sleeping_night: "Sleeping nights", respite: "Respite", other: "Other",
};
const START_TIMING_LABELS = {
  asap: "As soon as possible", within_1_month: "Within 1 month",
  "1_to_3_months": "1–3 months", "3_to_6_months": "3–6 months",
  flexible: "Flexible", exact_date: "On an exact date",
};
const DURATION_LABELS = {
  one_off: "One-off", under_1_month: "Under 1 month", "1_to_3_months": "1–3 months",
  "3_to_6_months": "3–6 months", "6_to_12_months": "6–12 months", ongoing: "Ongoing",
};
const HOURS_LABELS = {
  under_10: "Under 10 h/wk", "10_to_20": "10–20 h/wk", "20_to_40": "20–40 h/wk",
  "40_to_60": "40–60 h/wk", "60_plus": "60+ h/wk", live_in: "Live-in",
};
const BUDGET_LABELS = {
  under_15_per_hour: "Under £15/h", "15_to_20_per_hour": "£15–£20/h",
  "20_to_25_per_hour": "£20–£25/h", "25_plus_per_hour": "£25+/h",
  under_150_per_day: "Under £150/day", "150_to_200_per_day": "£150–£200/day",
  "200_to_250_per_day": "£200–£250/day", "250_plus_per_day": "£250+/day",
  flexible: "Flexible / open",
};
const FUNDING_LABELS = {
  private: "Private / self-funded", direct_payment: "Direct payment",
  la_nhs: "Local authority or NHS-related", unsure: "Unsure",
};
const ESSENTIAL_LABELS = {
  driver_required: "Carer must be a driver", own_car_required: "Carer needs own car",
  pet_friendly: "Pet-friendly household", no_pets_household: "No pets in household",
  smoker_friendly: "Smoker-friendly", non_smoking_household: "Non-smoking household",
  moving_and_handling: "Moving and handling", waking_night_required: "Waking nights required",
  dementia_experience: "Dementia experience", end_of_life_experience: "End-of-life experience",
  peg_or_stoma_experience: "PEG/stoma experience", hoisting_experience: "Hoisting experience",
  medication_administration: "Medication administration", female_carer_only: "Female carer only",
  male_carer_only: "Male carer only", second_language_required: "Second language needed",
};

function fmtDate(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  } catch { return null; }
}

function Chip({ children }) {
  return <span className="pill bg-slate-100 text-ink text-xs">{children}</span>;
}

function OpportunityCard({ o, canRespond, onExpressInterest }) {
  const posted = fmtDate(o.posted_at);
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState(o.interest_status || null);
  const [expanded, setExpanded] = useState(false);
  const hasFullDetails = !!(o.support_needs || o.night_expectations || o.additional_details);

  const handleClick = async () => {
    if (status || submitting) return;
    setSubmitting(true);
    try {
      await onExpressInterest(o.post_id);
      setStatus("submitted");
      toast.success("Interest sent to the family.");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Could not submit your interest. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="card-base p-5 md:p-6" data-testid={`public-opportunity-card-${o.post_id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2 text-ink font-display font-semibold text-lg">
          <MapPin className="w-4 h-4 text-teal shrink-0" strokeWidth={1.75} />
          {o.location_area || "Location not specified"}
        </div>
        {posted && <span className="text-xs text-ink-muted shrink-0">Listed {posted}</span>}
      </div>

      <div className="mt-3 flex flex-wrap gap-2">
        <Chip>{CARE_TYPE_LABELS[o.care_type] || o.care_type_label || "Care type not specified"}</Chip>
        {o.hours_per_week_band && <Chip>{HOURS_LABELS[o.hours_per_week_band] || o.hours_per_week_band}</Chip>}
        {o.duration_band && <Chip>{DURATION_LABELS[o.duration_band] || o.duration_band}</Chip>}
        {o.budget_band && <Chip>{BUDGET_LABELS[o.budget_band] || o.budget_band}</Chip>}
      </div>

      <div className="mt-4 grid sm:grid-cols-2 gap-3 text-sm text-ink-muted">
        {o.preferred_start_timing && (
          <div className="flex items-center gap-2">
            <CalendarClock className="w-4 h-4 text-purple shrink-0" strokeWidth={1.75} />
            {START_TIMING_LABELS[o.preferred_start_timing] || o.preferred_start_timing}
            {o.start_date ? ` (${o.start_date})` : ""}
          </div>
        )}
        {o.rota_pattern && (
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-purple shrink-0" strokeWidth={1.75} />
            {o.rota_pattern}
          </div>
        )}
        {o.funding_type && (
          <div className="flex items-center gap-2">
            <PoundSterling className="w-4 h-4 text-purple shrink-0" strokeWidth={1.75} />
            {FUNDING_LABELS[o.funding_type] || o.funding_type}
          </div>
        )}
      </div>

      {(o.essential_requirements || []).length > 0 && (
        <div className="mt-4 pt-4 border-t border-slate-100">
          <div className="text-xs font-semibold text-ink-muted mb-2">Essential requirements</div>
          <div className="flex flex-wrap gap-1.5">
            {o.essential_requirements.map((r) => (
              <Chip key={r}>{ESSENTIAL_LABELS[r] || r}</Chip>
            ))}
          </div>
        </div>
      )}

      {hasFullDetails && (
        <div className="mt-4 pt-4 border-t border-slate-100">
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="text-sm font-medium text-purple hover:underline inline-flex items-center gap-1"
            data-testid={`public-opportunity-view-details-${o.post_id}`}
          >
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            {expanded ? "Hide full details" : "View full details"}
          </button>
          {expanded && (
            <div className="mt-3 space-y-3 text-sm text-ink-muted" data-testid={`public-opportunity-full-details-${o.post_id}`}>
              {o.support_needs && (
                <div>
                  <div className="text-xs font-semibold text-ink mb-1">Support needs</div>
                  <p className="whitespace-pre-wrap leading-relaxed">{o.support_needs}</p>
                </div>
              )}
              {o.night_expectations && (
                <div>
                  <div className="text-xs font-semibold text-ink mb-1">Night expectations</div>
                  <p className="whitespace-pre-wrap leading-relaxed">{o.night_expectations}</p>
                </div>
              )}
              {o.additional_details && (
                <div>
                  <div className="text-xs font-semibold text-ink mb-1">Additional details</div>
                  <p className="whitespace-pre-wrap leading-relaxed">{o.additional_details}</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <div className="mt-5 pt-4 border-t border-slate-100 flex items-center justify-between gap-3">
        <p className="text-xs text-ink-muted max-w-xs">
          JCC does not share contact details or create a Care Room automatically.
        </p>
        {!canRespond ? (
          <Link
            to={`/auth/handover/request?next=${encodeURIComponent("/workspace/pro-carer-circle/care-opportunities")}`}
            className="btn-outline text-sm inline-flex items-center gap-1.5 shrink-0"
            data-testid={`public-opportunity-signin-${o.post_id}`}
          >
            <LogIn className="w-4 h-4" strokeWidth={1.75} /> Sign in to express interest
          </Link>
        ) : status === "accepted" ? (
          <span
            className="inline-flex items-center gap-1.5 rounded-full bg-teal/10 text-teal px-4 py-2 text-sm font-semibold shrink-0"
            data-testid={`public-opportunity-interest-accepted-${o.post_id}`}
          >
            <CheckCircle2 className="w-4 h-4" strokeWidth={2} /> Introduction accepted
          </span>
        ) : status === "declined" ? (
          <span
            className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 text-ink-muted px-4 py-2 text-sm font-medium shrink-0"
            data-testid={`public-opportunity-interest-declined-${o.post_id}`}
          >
            Not progressed
          </span>
        ) : status === "submitted" ? (
          <span
            className="inline-flex items-center gap-1.5 rounded-full bg-teal/10 text-teal px-4 py-2 text-sm font-semibold shrink-0"
            data-testid={`public-opportunity-interest-submitted-${o.post_id}`}
          >
            <CheckCircle2 className="w-4 h-4" strokeWidth={2} /> Interest submitted
          </span>
        ) : (
          <button
            className="btn-teal text-sm inline-flex items-center gap-1.5 shrink-0 disabled:opacity-60"
            onClick={handleClick}
            disabled={submitting}
            data-testid={`public-opportunity-interest-btn-${o.post_id}`}
          >
            <HandHeart className="w-4 h-4" strokeWidth={1.75} />
            {submitting ? "Submitting…" : "I'm interested"}
          </button>
        )}
      </div>

      {status === "submitted" && (
        <div
          className="mt-3 rounded-xl bg-teal/5 border border-teal/20 p-3.5 flex items-start gap-2.5 text-sm text-ink-muted"
          data-testid={`public-opportunity-interest-confirmation-${o.post_id}`}
        >
          <Info className="w-4 h-4 text-teal shrink-0 mt-0.5" strokeWidth={1.75} />
          <p className="leading-relaxed">
            Your interest has been sent to the family — they've been notified by email and can review your
            profile. No contact details are shared automatically and no Care Discussion Room is created unless
            they invite you. You'll get an email if they accept or don't progress this. We've also sent you a
            confirmation email.
          </p>
        </div>
      )}
    </div>
  );
}

export default function CareNeedsBrowse() {
  const [opportunities, setOpportunities] = useState(null); // null = loading
  const [error, setError] = useState(false);
  const [signedInCarer, setSignedInCarer] = useState(false);
  const { ready, active } = useHandoverSession();

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/api/care-needs/public");
        if (!alive) return;
        setOpportunities(data.opportunities || []);
        setSignedInCarer(!!data.signed_in_carer);
      } catch {
        if (alive) setError(true);
      }
    })();
    return () => { alive = false; };
  }, []);

  const expressInterest = async (postId) => {
    await api.post(`/api/care-needs/${postId}/interest`);
  };

  return (
    <div data-testid="page-care-needs-browse">
      <PageHero
        eyebrow="Care Opportunities"
        title="Browse Care Needs"
        description="Published Care Needs from families looking for independent care. Free to browse for everyone — sign in or join free as a JCC Carer to express interest. No paid membership required, at any tier."
      >
        {!active && ready && (
          <Link to="/join/carer-starter" className="btn-teal text-sm" data-testid="care-needs-browse-join-free">
            Join free as a Carer
          </Link>
        )}
      </PageHero>

      <section className="section pt-8 pb-20">
        <div className="container-jcc max-w-4xl">
          {opportunities === null && !error && (
            <p className="text-ink-muted" data-testid="care-needs-browse-loading">Loading care opportunities…</p>
          )}
          {error && (
            <p className="text-ink-muted" data-testid="care-needs-browse-error">
              We couldn't load care opportunities right now. Please try again shortly.
            </p>
          )}
          {opportunities !== null && opportunities.length === 0 && (
            <div className="rounded-2xl border border-dashed border-slate-200 p-10 text-center" data-testid="care-needs-browse-empty">
              <div className="w-12 h-12 rounded-xl bg-teal/10 text-teal flex items-center justify-center mx-auto mb-4">
                <Compass className="w-6 h-6" strokeWidth={1.75} />
              </div>
              <h2 className="font-display font-semibold text-lg">No care opportunities are published right now</h2>
              <p className="mt-2 text-ink-muted max-w-md mx-auto">
                There are currently no published Care Needs to browse. New opportunities will appear
                here as submitted Care Needs are reviewed and approved for publication — please check back soon.
              </p>
            </div>
          )}
          {opportunities !== null && opportunities.length > 0 && (
            <div className="space-y-4" data-testid="care-needs-browse-list">
              {opportunities.map((o) => (
                <OpportunityCard key={o.post_id} o={o} canRespond={signedInCarer} onExpressInterest={expressInterest} />
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
